import classNames from 'classnames';
import $ from 'jquery';
import React from 'react';

type Props = {
  className?: string,
  id: string,
  wrapperClassName?: string,
  iconClassName?: string,
  content?: React.ReactNode,
  alignRight?: boolean,
  children?: React.ReactNode,
  type?: string,
  centered?: boolean,
  disabled?: boolean,
  hideCaret?: boolean,
  tooltip?: string,
  menuClassName?: string,

  onClick?: (event: React.MouseEvent<HTMLButtonElement> | null) => void
};

type State = {
  dropType: string,
  mode: string,
  openMenuId: number
};

type DefaultProps = Required<Pick<Props, 'type'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class DropdownMenu extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    type: 'btn-default'
  };

  constructor (props: Props) {
    super(props);

    this.state = {
      dropType: 'esko_dropdown',
      mode: '',
      openMenuId: 0
    };

    this.handleDropdownClick = this.handleDropdownClick.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.fixDropDownSize = this.fixDropDownSize.bind(this);
  }

  override componentDidMount (): void {
    this.fixDropDownSize();
    window.addEventListener('resize', this.fixDropDownSize);
  }

  override componentWillUnmount (): void {
    window.removeEventListener('resize', this.fixDropDownSize);
    window.removeEventListener('mousedown', this.handleMouseDown);
  }

  override componentDidUpdate (): void {
    this.fixDropDownSize();
  }

  fixOpenDropdownSize (event: React.MouseEvent<HTMLButtonElement> | null) {
    const { id } = (this.props as PropsWithDefaults);

    const top = $('#' + id)?.offset()?.top || 0;

    let pageContainer = $('.esko_pagecontainer-nopadding');
    if (pageContainer[0] == null) {
      pageContainer = $('.esko_pagecontainer');
    }

    if (pageContainer == null) {
      return;
    }

    const topWrapper = pageContainer[0] != null ? pageContainer.offset()?.top ?? 0 : 0;
    const bottom = top + ($('#' + id).outerHeight(true) ?? 0);
    const spaceAboveButton = top - topWrapper - 4;
    const spaceBelowButton = ($(window)?.height() ?? 0) - bottom - 4;
    const menuHeight = $('#' + id + '_menu').prop('scrollHeight') ?? 0;
    const entryHeight = $('#' + id + '_menu').children().first().outerHeight() ?? 0;
    let spaceForDropDown: number;

    if (this.props.onClick != null) {
      if ((spaceBelowButton < menuHeight && spaceAboveButton > menuHeight) || (spaceBelowButton < menuHeight && spaceAboveButton < menuHeight && spaceAboveButton > spaceBelowButton)) {
        spaceForDropDown = spaceAboveButton;

        if (this.state.dropType === 'esko_dropdown') {
          this.setState({dropType: 'esko_dropup'});
        }
      } else {
        spaceForDropDown = spaceBelowButton;

        if (this.state.dropType === 'esko_dropup') {
          this.setState({dropType: 'esko_dropdown'});
        }
      }

      $('#' + id + '_menu').css('max-height', spaceForDropDown);
      $('#' + id + '_menu').css('overflow-y', 'auto');

      if (this.props.onClick != null) {
        this.props.onClick(event);
      }
    } else {
      if ((spaceBelowButton < spaceAboveButton) &&
      (spaceBelowButton < menuHeight) &&
      (spaceBelowButton < (2 * entryHeight + 4))) {
        spaceForDropDown = spaceAboveButton;

        if (this.state.dropType === 'esko_dropdown') {
          // At this point, the menu is open, so add the open class to prevent the menu from closing when repositioning/-sizing.
          this.setState({dropType: 'esko_dropup'});
        }
      } else {
        spaceForDropDown = spaceBelowButton;

        if (this.state.dropType === 'esko_dropup') {
          this.setState({dropType: 'esko_dropdown'});
        }
      }

      $('#' + id + '_menu').css('max-height', spaceForDropDown - 15);
      $('#' + id + '_menu').css('overflow-y', 'auto');
    }
  }

  handleMouseDown (event: MouseEvent) {
    const { id } = this.props;

    let clickOutside = true;
    if (event.target != null && event.target instanceof HTMLElement) {
      const mouseTarget = event.target;

      if (mouseTarget.closest(`#${id}_menu`) != null) {
        clickOutside = false;
      }
    }

    if (clickOutside) {
      const openMenuIdOnBlur: number = this.state.openMenuId;

      setTimeout(() => {
        if (this.state.mode !== '' && this.state.openMenuId === openMenuIdOnBlur) {
          this.setState({mode: ''});

          window.removeEventListener('mousedown', this.handleMouseDown);
        }
      }, 200);
    }
  }

  handleDropdownClick (event: React.MouseEvent<HTMLButtonElement>) {
    if (this.state.mode !== 'open') {
      this.setState({mode: 'open', openMenuId: this.state.openMenuId + 1});
      this.fixOpenDropdownSize(event);
      
      window.addEventListener('mousedown', this.handleMouseDown);
    } else {
      this.setState({mode: ''});
      event.stopPropagation();

      window.removeEventListener('mousedown', this.handleMouseDown);
    }
  }

  fixDropDownSize () {
    if (this.state.mode === 'open') {
      this.fixOpenDropdownSize(null);
    }
  }

  override render (): React.ReactNode {
    const { className, id, wrapperClassName, iconClassName, content,
      alignRight, children, type, centered, disabled, hideCaret,
      tooltip, menuClassName } = (this.props as PropsWithDefaults);

    const buttonClassName = classNames(
      className,
      'esko_btn',
      type.split(' ').map(className => 'esko_' + className).join(' '),
      'esko_dropdown-toggle',
      {'esko_dropdownbutton-stretch': !centered}
    );

    const ulClassName = classNames(
      'esko_dropdown-menu',
      {'esko_dropdown-menu-right': alignRight},
      menuClassName
    );
    
    return (
      <div id={id + '_wrapper'}
        className={classNames(this.state.dropType, this.state.mode !== '' ? 'esko_' + this.state.mode : null, wrapperClassName)}
      >
        <button id={id} type='button'
          onClick={this.handleDropdownClick}
          className={buttonClassName}
          aria-haspopup='true'
          aria-expanded='false'
          disabled={disabled}
          title={tooltip}>
          {hideCaret
          ? <div>{iconClassName && <span className={iconClassName} />}</div>
          : centered
            ? <div>
              {iconClassName && <span className={iconClassName} />}
              {content}{' '}<span className='esko_caret' />
            </div>
            : [
              <div key='1' className='esko_dropdowntext'>
                {iconClassName && <span className={iconClassName} />}
                {content}
              </div>,
              <div key='2'>
                {' '}<span className='esko_caret' />
              </div>
            ]
          }
        </button>
        <ul id={id + '_menu'} className={ulClassName} aria-labelledby={id}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)){
              if (typeof child.props.onClick === 'function') {
                return React.cloneElement(child as any, {
                  onClick: (...args: any[]) => {
                    child.props.onClick(...args);
                    this.setState({mode: ''});
                  }
                });
              } else {
                return React.cloneElement(child as any, {
                  onClick: () => {
                    this.setState({mode: ''});
                  }
                });
              }
            }

            return child;
          })}
        </ul>
      </div>
    );
  }
}

export default DropdownMenu;
